export default function Button({
  children,
  icon,
  iconPosition = "right",
  isLoading = false,
  onClick,
  leftIcon,
  rightIcon,
  size = "lg",
  type = "button",
  variant = "unstyled",
  disabled = false,
  className = "",
  ...props
}) {
  const baseClasses =
    "inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-lg font-sf-medium transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]/25";

  const sizes = {
    sm: "min-h-8 px-3 py-1 text-xs gap-1.5",
    md: "min-h-9 px-4 py-1 text-sm gap-2",
    lg: "min-h-[42px] px-4 py-2 text-sm gap-2",
    xl: "min-h-12 px-5 py-3 text-base gap-2",
  };

  const variants = {
    unstyled: "",
    darkBlue:
      "border border-[#1E40AF] bg-[#1E40AF] text-white hover:bg-[#1E3A8A]",
    orange:
      "border border-[#F97316] bg-[#F97316] text-white hover:bg-[#EA580C]",
    neutral:
      "border border-none bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400",
    primary:
      "border border-[#2563EB] bg-[#2563EB] text-white hover:bg-[#1D4ED8] active:bg-[#1E40AF]",
    secondary:
      "border border-transparent bg-[#F3F4F6] text-[#374151] hover:bg-[#E5E7EB]",
    more:
      "border border-transparent bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]",
    outline:
      "border border-[#2563EB] bg-white text-[#2563EB] hover:bg-[#EFF6FF]",
    outlineConfig:
      "border border-[#2563EB] bg-[#EFF6FF] text-[#2563EB] hover:bg-[#DBEAFE]",
    danger:
      "border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] hover:bg-[#FEE2E2]",
    dangerFilled:
      "border border-[#DC2626] bg-[#DC2626] text-white hover:bg-red-700",
    success:
      "border border-[#16A34A] bg-[#16A34A] text-white hover:bg-[#15803D]",
  };

  const disabledStyles = "cursor-not-allowed opacity-60 pointer-events-none";

  const resolvedLeftIcon =
    leftIcon || (iconPosition === "left" ? icon : undefined);
  const resolvedRightIcon =
    rightIcon || (iconPosition === "right" ? icon : undefined);

  const renderIcon = (node) =>
    node ? (
      <span className="flex size-5 items-center justify-center">{node}</span>
    ) : null;

  return (
    <button
      type={type}
      onClick={disabled ? undefined : onClick}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      className={`
        ${variant === "unstyled" ? "inline-flex items-center justify-center" : baseClasses}
        ${variant === "unstyled" ? "" : sizes[size] || sizes.md}
        ${variants[variant] || variants.unstyled}
        ${
          disabled || isLoading
            ? disabledStyles
            : variant === "unstyled"
              ? "cursor-pointer"
              : "cursor-pointer active:opacity-90"
        }
        ${className}
      `}
      {...props}
    >
      {isLoading ? (
        <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        renderIcon(resolvedLeftIcon)
      )}
      {children}
      {!isLoading && renderIcon(resolvedRightIcon)}
    </button>
  );
}
